import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Calendar, Users, PoundSterling, Plus, Edit, Trash2, Save, X,
  Clock, MapPin, ArrowLeft, Image, Star, UserPlus, Check, Upload, Loader2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../lib/api';

const BootcampDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [bootcamp, setBootcamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    price: 275,
    capacity: 30,
    age_min: 6,
    age_max: 18,
    location: '',
    image_url: '',
    gallery: [],
    features: [],
    what_to_bring: [],
    guest_teachers: [],
    daily_schedule: '',
    is_active: true
  });
  
  // Input states for adding items
  const [newFeature, setNewFeature] = useState('');
  const [newWhatToBring, setNewWhatToBring] = useState('');
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Shared file-upload helper. Use XMLHttpRequest directly to sidestep the
  // default `Content-Type: application/json` on our axios instance, which
  // would otherwise JSON-serialize the FormData and cause a 422 on the server.
  const uploadImageFile = (file) => new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/api/site-media/upload/persistent`, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (!data?.url) return reject(new Error('Upload returned no URL'));
          const url = data.url.startsWith('http') ? data.url : apiBase + data.url;
          resolve(url);
        } catch (e) {
          reject(new Error('Invalid server response'));
        }
      } else {
        let detail;
        try { detail = JSON.parse(xhr.responseText)?.detail; } catch (e) { detail = xhr.statusText; }
        reject({ response: { data: { detail } }, message: detail || `HTTP ${xhr.status}` });
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });

  const uploadCoverFile = async (file) => {
    if (!/^image\//.test(file.type)) {
      toast.error('Please drop an image file');
      return;
    }
    try {
      setUploadingCover(true);
      const url = await uploadImageFile(file);
      setFormData(prev => ({ ...prev, image_url: url }));
      toast.success('Cover image uploaded');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || '';
      if (/too large/i.test(detail) || /size/i.test(detail) || err?.message === 'Network error') {
        toast.error('Image is too large. Please use a photo under 25 MB.');
      } else if (/invalid image type/i.test(detail)) {
        toast.error('Unsupported file type. Please use JPG, PNG, WebP, GIF or HEIC.');
      } else {
        toast.error(detail || 'Upload failed');
      }
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadGalleryFiles = async (files) => {
    const images = Array.from(files).filter(f => /^image\//.test(f.type));
    if (!images.length) {
      toast.error('No image files found');
      return;
    }
    setUploadingGallery(true);
    try {
      const newUrls = [];
      for (const f of images) {
        try {
          newUrls.push(await uploadImageFile(f));
        } catch (err) {
          const detail = err.response?.data?.detail || err.message || '';
          // Clear, friendly size errors instead of silent failure.
          let msg;
          if (/too large/i.test(detail) || /size/i.test(detail) || err?.message === 'Network error') {
            msg = `${f.name}: image is too large. Please use a photo under 25 MB (typical phone photos are fine).`;
          } else if (/invalid image type/i.test(detail)) {
            msg = `${f.name}: unsupported file type. Please use JPG, PNG, WebP, GIF or HEIC.`;
          } else {
            msg = `${f.name}: ${detail || 'upload failed'}`;
          }
          toast.error(msg);
        }
      }
      if (newUrls.length) {
        setFormData(prev => ({ ...prev, gallery: [...(prev.gallery || []), ...newUrls] }));
        toast.success(`${newUrls.length} photo(s) added`);
      }
    } finally {
      setUploadingGallery(false);
    }
  };
  const [newTeacher, setNewTeacher] = useState({
    name: '', role: '', bio: '', image_url: '', credits: []
  });
  const [newCredit, setNewCredit] = useState('');

  useEffect(() => {
    fetchBootcamp();
  }, [id]);

  const fetchBootcamp = async () => {
    try {
      const response = await api.get(`/bootcamps/${id}`);
      setBootcamp(response.data);
      setFormData({
        name: response.data.name || '',
        description: response.data.description || '',
        start_date: response.data.start_date || '',
        end_date: response.data.end_date || '',
        price: response.data.price || 275,
        capacity: response.data.capacity || 30,
        age_min: response.data.age_min || 6,
        age_max: response.data.age_max || 18,
        location: response.data.location || '',
        image_url: response.data.image_url || '',
        gallery: response.data.gallery || [],
        features: response.data.features || [],
        what_to_bring: response.data.what_to_bring || [],
        guest_teachers: response.data.guest_teachers || [],
        daily_schedule: response.data.daily_schedule || '',
        is_active: response.data.is_active ?? true
      });
    } catch (error) {
      toast.error('Failed to load bootcamp');
      navigate('/admin/bootcamps');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/bootcamps/${id}`, formData);
      toast.success('Bootcamp updated successfully');
      setEditMode(false);
      fetchBootcamp();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: bootcamp.name || '',
      description: bootcamp.description || '',
      start_date: bootcamp.start_date || '',
      end_date: bootcamp.end_date || '',
      price: bootcamp.price || 275,
      capacity: bootcamp.capacity || 30,
      age_min: bootcamp.age_min || 6,
      age_max: bootcamp.age_max || 18,
      location: bootcamp.location || '',
      image_url: bootcamp.image_url || '',
      gallery: bootcamp.gallery || [],
      features: bootcamp.features || [],
      what_to_bring: bootcamp.what_to_bring || [],
      guest_teachers: bootcamp.guest_teachers || [],
      daily_schedule: bootcamp.daily_schedule || '',
      is_active: bootcamp.is_active ?? true
    });
    setEditMode(false);
  };

  // Helper functions for managing lists
  const addFeature = () => {
    if (!newFeature.trim()) return;
    setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
    setNewFeature('');
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const addWhatToBring = () => {
    if (!newWhatToBring.trim()) return;
    setFormData({ ...formData, what_to_bring: [...formData.what_to_bring, newWhatToBring.trim()] });
    setNewWhatToBring('');
  };

  const removeWhatToBring = (index) => {
    setFormData({ ...formData, what_to_bring: formData.what_to_bring.filter((_, i) => i !== index) });
  };

  const addGalleryImage = () => {
    if (!newGalleryUrl.trim()) return;
    setFormData({ ...formData, gallery: [...formData.gallery, newGalleryUrl.trim()] });
    setNewGalleryUrl('');
  };

  const removeGalleryImage = (index) => {
    setFormData({ ...formData, gallery: formData.gallery.filter((_, i) => i !== index) });
  };

  const addCredit = () => {
    if (!newCredit.trim()) return;
    setNewTeacher({ ...newTeacher, credits: [...newTeacher.credits, newCredit.trim()] });
    setNewCredit('');
  };

  const addGuestTeacher = () => {
    if (!newTeacher.name.trim()) {
      toast.error('Please enter a name for the guest teacher');
      return;
    }
    setFormData({ ...formData, guest_teachers: [...formData.guest_teachers, { ...newTeacher }] });
    setNewTeacher({ name: '', role: '', bio: '', image_url: '', credits: [] });
  };

  const removeGuestTeacher = (index) => {
    setFormData({ ...formData, guest_teachers: formData.guest_teachers.filter((_, i) => i !== index) });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/bootcamps')}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            {editMode ? (
              <>
                <Button variant="outline" onClick={handleCancel} className="border-neutral-700">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setEditMode(true)} className="bg-purple-500 hover:bg-purple-600">
                <Edit className="w-4 h-4 mr-2" />
                Edit Bootcamp
              </Button>
            )}
          </div>
        </div>

        {/* Cover Image Section */}
        <div className="mb-8">
          <div className="relative rounded-xl overflow-hidden bg-neutral-900 h-64">
            {formData.image_url ? (
              <img 
                src={formData.image_url} 
                alt={formData.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
                <Image className="w-16 h-16 text-white/30" />
              </div>
            )}
            
            {editMode && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-4" data-testid="cover-image-edit">
                <div className="bg-[#0a0a0a] border border-[#333] p-5 rounded-lg w-full max-w-md space-y-3">
                  <div>
                    <Label className="text-white text-sm font-medium">Cover Image</Label>
                    <p className="text-[11px] text-white/50 mt-1">Recommended: 1600 × 600 px (landscape banner). JPG / PNG / WebP, max 5 MB.</p>
                  </div>

                  {/* Current preview */}
                  {formData.image_url && (
                    <div className="rounded-md overflow-hidden border border-[#333] bg-[#111]">
                      <img
                        src={formData.image_url}
                        alt="Cover preview"
                        className="w-full h-24 object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  )}

                  {/* Drop zone + file input */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400','bg-purple-500/10'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-purple-400','bg-purple-500/10'); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-purple-400','bg-purple-500/10');
                      const f = e.dataTransfer.files?.[0];
                      if (!f) return;
                      await uploadCoverFile(f);
                    }}
                    className="border-2 border-dashed border-[#444] rounded-md p-3 transition-colors"
                  >
                    <Label className="text-white/80 text-xs">Drop an image here, or choose a file</Label>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      data-testid="cover-image-upload-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        await uploadCoverFile(f);
                        e.target.value = '';
                      }}
                      className="block w-full text-sm text-white/80 mt-2 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-purple-500 file:text-white hover:file:bg-purple-600 file:cursor-pointer"
                    />
                    {uploadingCover && (
                      <div className="flex items-center gap-2 text-xs text-purple-300 mt-2" data-testid="cover-uploading">
                        <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                      </div>
                    )}
                  </div>

                  <div className="text-center text-xs text-white/40">— or —</div>

                  {/* URL fallback */}
                  <div>
                    <Label className="text-white/80 text-xs">Paste image URL</Label>
                    <Input
                      value={formData.image_url || ''}
                      onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                      className="bg-[#111] border-[#333] text-white placeholder:text-white/40 mt-1"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              {editMode ? (
                <Input
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="bg-transparent border-none text-3xl font-bold text-white p-0 focus:ring-0"
                  placeholder="Bootcamp Name"
                />
              ) : (
                <h1 className="text-3xl font-bold">{formData.name}</h1>
              )}
              
              <div className="flex items-center gap-4 mt-2 text-white/70">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(formData.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(formData.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {formData.location}
                </span>
                <Badge className={formData.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {formData.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white">About This Bootcamp</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 min-h-[150px]"
                    placeholder="Describe the bootcamp experience..."
                  />
                ) : (
                  <p className="text-white/70 whitespace-pre-wrap">{formData.description || 'No description added yet.'}</p>
                )}
              </CardContent>
            </Card>

            {/* Photo Gallery */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Image className="w-5 h-5 text-purple-400" />
                  Photo Gallery
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode && (
                  <div className="space-y-3 mb-4">
                    <p className="text-[11px] text-white/50">Recommended: 1200 × 800 px or larger, landscape. JPG / PNG / WebP, max 5 MB each.</p>

                    {/* Drag & drop + file picker */}
                    <div
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-purple-400','bg-purple-500/10'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('border-purple-400','bg-purple-500/10'); }}
                      onDrop={async (e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-purple-400','bg-purple-500/10');
                        if (e.dataTransfer.files?.length) {
                          await uploadGalleryFiles(e.dataTransfer.files);
                        }
                      }}
                      className="border-2 border-dashed border-[#444] rounded-md p-6 text-center transition-colors"
                      data-testid="gallery-drop-zone"
                    >
                      <Image className="w-8 h-8 text-white/30 mx-auto mb-2" />
                      <p className="text-sm text-white/70 mb-2">Drag & drop photos here</p>
                      <p className="text-xs text-white/40 mb-3">or choose files below</p>
                      <input
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/webp"
                        data-testid="gallery-upload-input"
                        onChange={async (e) => {
                          if (e.target.files?.length) {
                            await uploadGalleryFiles(e.target.files);
                          }
                          e.target.value = '';
                        }}
                        className="block w-full text-sm text-white/80 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-purple-500 file:text-white hover:file:bg-purple-600 file:cursor-pointer"
                      />
                      {uploadingGallery && (
                        <div className="flex items-center justify-center gap-2 text-xs text-purple-300 mt-2" data-testid="gallery-uploading">
                          <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                        </div>
                      )}
                    </div>

                    <div className="text-center text-xs text-white/40">— or paste a URL —</div>
                    <div className="flex gap-2">
                      <Input
                        value={newGalleryUrl}
                        onChange={e => setNewGalleryUrl(e.target.value)}
                        className="bg-[#111] border-[#333] text-white placeholder:text-white/40 flex-1"
                        placeholder="https://example.com/photo.jpg"
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addGalleryImage())}
                      />
                      <Button onClick={addGalleryImage} className="bg-purple-500 hover:bg-purple-600">
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                )}
                
                {formData.gallery && formData.gallery.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {formData.gallery.map((url, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden aspect-video bg-[#0a0a0a] border border-[#222]">
                        <img
                          src={url}
                          alt={`Gallery ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const sib = e.currentTarget.nextElementSibling;
                            if (sib) sib.style.display = 'flex';
                          }}
                        />
                        <div
                          className="absolute inset-0 hidden flex-col items-center justify-center text-white/40 text-[10px] p-2 text-center break-all"
                        >
                          <Image className="w-6 h-6 mb-1" />
                          Failed to load
                          <span className="opacity-50">{url}</span>
                        </div>
                        {editMode && (
                          <button
                            onClick={() => removeGalleryImage(idx)}
                            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-white/40">
                    <Image className="w-12 h-12 mx-auto mb-2" />
                    <p>No photos added yet</p>
                    {editMode && <p className="text-sm mt-1">Add image URLs above to create a gallery</p>}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guest Teachers */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Star className="w-5 h-5 text-purple-400" />
                  Guest Teachers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Existing teachers */}
                {formData.guest_teachers && formData.guest_teachers.length > 0 && (
                  <div className="space-y-4 mb-6">
                    {formData.guest_teachers.map((teacher, idx) => (
                      <div key={idx} className="flex gap-4 p-4 bg-neutral-800/50 rounded-lg">
                        {teacher.image_url ? (
                          <img src={teacher.image_url} alt={teacher.name} className="w-20 h-20 rounded-lg object-cover" />
                        ) : (
                          <div className="w-20 h-20 rounded-lg bg-purple-500/20 flex items-center justify-center">
                            <Star className="w-8 h-8 text-purple-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-white">{teacher.name}</h4>
                              {teacher.role && <p className="text-purple-400 text-sm">{teacher.role}</p>}
                            </div>
                            {editMode && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeGuestTeacher(idx)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {teacher.bio && <p className="text-white/60 text-sm mt-2">{teacher.bio}</p>}
                          {teacher.credits && teacher.credits.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {teacher.credits.map((credit, cidx) => (
                                <Badge key={cidx} variant="secondary" className="text-xs">
                                  {credit}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new teacher form */}
                {editMode && (
                  <div className="border border-neutral-700 rounded-lg p-4 space-y-3">
                    <p className="text-sm font-medium text-purple-400">Add New Guest Teacher</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        value={newTeacher.name}
                        onChange={e => setNewTeacher({ ...newTeacher, name: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                        placeholder="Name *"
                      />
                      <Input
                        value={newTeacher.role}
                        onChange={e => setNewTeacher({ ...newTeacher, role: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                        placeholder="Role (e.g., Choreographer)"
                      />
                    </div>
                    
                    <Textarea
                      value={newTeacher.bio}
                      onChange={e => setNewTeacher({ ...newTeacher, bio: e.target.value })}
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                      placeholder="Short bio..."
                      rows={2}
                    />
                    
                    <Input
                      value={newTeacher.image_url}
                      onChange={e => setNewTeacher({ ...newTeacher, image_url: e.target.value })}
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40"
                      placeholder="Photo URL"
                    />
                    
                    <div className="flex gap-2">
                      <Input
                        value={newCredit}
                        onChange={e => setNewCredit(e.target.value)}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 flex-1"
                        placeholder="Add show credit (e.g., Les Misérables)"
                        onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addCredit())}
                      />
                      <Button type="button" onClick={addCredit} variant="outline" className="border-neutral-700">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {newTeacher.credits.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {newTeacher.credits.map((credit, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {credit}
                            <button
                              onClick={() => setNewTeacher({
                                ...newTeacher,
                                credits: newTeacher.credits.filter((_, i) => i !== idx)
                              })}
                              className="ml-1"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <Button onClick={addGuestTeacher} className="w-full bg-purple-500/20 hover:bg-purple-500/30 text-purple-300">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Guest Teacher
                    </Button>
                  </div>
                )}

                {!editMode && (!formData.guest_teachers || formData.guest_teachers.length === 0) && (
                  <div className="text-center py-8 text-white/40">
                    <Star className="w-12 h-12 mx-auto mb-2" />
                    <p>No guest teachers added yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Daily Schedule */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-400" />
                  Daily Schedule
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea
                    value={formData.daily_schedule}
                    onChange={e => setFormData({ ...formData, daily_schedule: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 min-h-[150px]"
                    placeholder="9:00 AM - Registration & Warm-up&#10;10:00 AM - Dance Class&#10;11:30 AM - Break&#10;12:00 PM - Singing Workshop&#10;..."
                  />
                ) : (
                  <p className="text-white/70 whitespace-pre-wrap">{formData.daily_schedule || 'No schedule added yet.'}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/60 text-xs">Start Date</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                          className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs">End Date</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={e => setFormData({ ...formData, end_date: e.target.value })}
                          className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-white/60 text-xs">Price (£)</Label>
                      <Input
                        type="number"
                        value={formData.price}
                        onChange={e => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white/60 text-xs">Capacity</Label>
                      <Input
                        type="number"
                        value={formData.capacity}
                        onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 30 })}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white/60 text-xs">Location</Label>
                      <Input
                        value={formData.location}
                        onChange={e => setFormData({ ...formData, location: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-white/60 text-xs">Min Age</Label>
                        <Input
                          type="number"
                          value={formData.age_min}
                          onChange={e => setFormData({ ...formData, age_min: parseInt(e.target.value) || 6 })}
                          className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-white/60 text-xs">Max Age</Label>
                        <Input
                          type="number"
                          value={formData.age_max}
                          onChange={e => setFormData({ ...formData, age_max: parseInt(e.target.value) || 18 })}
                          className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2">
                      <Label className="text-white/60">Active</Label>
                      <Switch
                        checked={formData.is_active}
                        onCheckedChange={checked => setFormData({ ...formData, is_active: checked })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between py-2 border-b border-neutral-800">
                      <span className="text-white/60">Price</span>
                      <span className="text-2xl font-bold text-white">£{formData.price}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-neutral-800">
                      <span className="text-white/60">Capacity</span>
                      <span className="text-white">{bootcamp?.spots_remaining || formData.capacity} / {formData.capacity} spots</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-neutral-800">
                      <span className="text-white/60">Ages</span>
                      <span className="text-white">{formData.age_min} - {formData.age_max} years</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-white/60">Location</span>
                      <span className="text-white text-right">{formData.location}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white text-base">Features & Highlights</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode && (
                  <div className="flex gap-2 mb-3">
                    <Input
                      value={newFeature}
                      onChange={e => setNewFeature(e.target.value)}
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 flex-1 text-sm"
                      placeholder="Add feature..."
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <Button onClick={addFeature} size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {formData.features && formData.features.length > 0 ? (
                  <div className="space-y-2">
                    {formData.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-neutral-800/50 px-3 py-2 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-400" />
                          <span className="text-white/80 text-sm">{feature}</span>
                        </div>
                        {editMode && (
                          <button onClick={() => removeFeature(idx)} className="text-red-400 hover:text-red-300">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm text-center py-4">No features added</p>
                )}
              </CardContent>
            </Card>

            {/* What to Bring */}
            <Card className="bg-neutral-900 border-neutral-800">
              <CardHeader>
                <CardTitle className="text-white text-base">What to Bring</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode && (
                  <div className="flex gap-2 mb-3">
                    <Input
                      value={newWhatToBring}
                      onChange={e => setNewWhatToBring(e.target.value)}
                      className="bg-[#0a0a0a] border-[#333] text-white placeholder:text-white/40 flex-1 text-sm"
                      placeholder="Add item..."
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addWhatToBring())}
                    />
                    <Button onClick={addWhatToBring} size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {formData.what_to_bring && formData.what_to_bring.length > 0 ? (
                  <div className="space-y-2">
                    {formData.what_to_bring.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-neutral-800/50 px-3 py-2 rounded-lg">
                        <span className="text-white/80 text-sm">• {item}</span>
                        {editMode && (
                          <button onClick={() => removeWhatToBring(idx)} className="text-red-400 hover:text-red-300">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm text-center py-4">No items added</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BootcampDetail;
